/*
  LFY — colour image swatches on the product page.

  Drives the theme's OWN colour <select> from a strip of image swatches, then hides the
  dropdown. It deliberately implements no variant logic of its own: everything about
  matching a variant, updating the price, the gallery, the SKU, the stock widget and the
  Add to Cart button already lives in assets/variants.js, and all of it is reached by the
  one event a human picking from the dropdown would have produced:

      select.value = v; select.dispatchEvent(new Event('change', { bubbles: true }));

  MEASURED on preview theme 144754606164 BEFORE any of this was written, against the real
  page for hv3206-wht-mini-reces-white-round-recessed-led-step-light:
      input[name="id"]  39960203984980 -> 39960206671956
      [data-header-option]  "White" -> "Black"
  So the mechanism is proven on the live component, not assumed.

  ── WHAT THIS SCRIPT MUST NOT DO ────────────────────────────────────────────────
  Never move, rewrite or remove a node inside <variant-selects>. variants.js matches
  variants POSITIONALLY by DOM order (`this.options[index] === option`) and rewrites
  `[data-header-option]` on every `.product-form__input` it finds. The colour wrapper is
  hidden with an attribute + CSS so it stays in the DOM and stays writable.

  ── SCOPING (rule 6c: a DOM traversal is a contract with the markup) ─────────────
  Every lookup below starts from `.productView-variants`, a stable ancestor, never from
  parentElement/nextSibling hops. A previous defect in this theme killed Add to Cart
  because a presentational wrapper broke `sel.parentElement.querySelector(...)`.
*/
(function () {
  'use strict';

  var BOUND = 'lfyCswatchBound';

  function warn(strip, why) {
    // Loud, and the dropdown is left alone. A silent failure here is a product that
    // cannot be bought, so it must never be silent.
    if (window.console && console.warn) {
      console.warn('[lfy-color-swatch] not bound: ' + why, strip);
    }
    if (strip) strip.setAttribute('data-lfy-cswatch-error', why);
  }

  function bind(strip) {
    if (strip.dataset[BOUND] === '1') return;

    var scope = strip.closest('.productView-variants');
    if (!scope) return warn(strip, 'no .productView-variants ancestor');

    var picker = scope.querySelector('variant-selects, variant-radios');
    if (!picker) return warn(strip, 'no <variant-selects> in scope');

    var index = strip.getAttribute('data-option-index');
    var wrap = picker.querySelector('.product-form__input[data-option-index="' + index + '"]');
    if (!wrap) return warn(strip, 'no option wrapper at index ' + index);

    var select = wrap.querySelector('select');
    if (!select) return warn(strip, 'option ' + index + ' is not a <select> (picker_type is probably "button" — swatches are already native there)');

    var radios = Array.prototype.slice.call(strip.querySelectorAll('.lfy-cswatch__radio'));
    if (!radios.length) return warn(strip, 'strip has no swatches');

    // Guard against a mismatch between what the strip offers and what the select accepts.
    // If Liquid and the theme ever disagree about the option's values, a swatch would set
    // a value the select rejects, the select would silently keep its old value, and the
    // swatch would highlight without changing the variant — exactly the failure mode this
    // feature is most likely to hit. Refuse to bind instead.
    var selectValues = Array.prototype.map.call(select.options, function (o) { return o.value; });
    var orphan = radios.filter(function (r) { return selectValues.indexOf(r.value) === -1; });
    if (orphan.length) {
      return warn(strip, orphan.length + ' swatch value(s) absent from the <select>: ' +
        orphan.map(function (r) { return r.value; }).join(', '));
    }

    var current = strip.querySelector('[data-lfy-cswatch-current]');

    function optionFor(value) {
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value === value) return select.options[i];
      }
      return null;
    }

    // select -> swatches. Runs on every variant change whatever caused it: a swatch, the
    // URL's ?variant=, the mobile sticky bar, or variants.js auto-correcting away from a
    // sold-out combination.
    // `opts.trustMarkup` — do NOT touch the availability classes, only the selection and the
    // current-value label. Passed by the initial call. See the note on the availability block.
    function sync(opts) {
      var trustMarkup = !!(opts && opts.trustMarkup);
      var value = select.value;

      radios.forEach(function (radio) {
        radio.checked = (radio.value === value);

        var label = radio.nextElementSibling;
        if (!label || !label.classList.contains('product-form__label')) return;

        // Mirror ELLA'S OWN availability decision rather than recomputing it. variants.js
        // classes and relabels each <option>, so reading it back keeps one source of truth
        // and stays correct for multi-option products where availability depends on the
        // other selections.
        //
        // ⛔ BUT NOT ON THE FIRST CALL, AND THAT IS THE WHOLE BUG THIS GUARD FIXES.
        // `bind()` ends with sync(), which runs at DOMContentLoaded — BEFORE variants.js has
        // added `soldout` to any <option>. Every option therefore looked available, and this
        // block overwrote the CORRECT server-rendered class on every swatch. Measured on live
        // on arvin and gilson: Liquid emitted `soldout` for antique gold and Chrome (proved
        // with a temporary server-rendered debug attribute), the DOM showed `available` on all
        // three, and the <option> elements DID carry class="soldout" by the time anything was
        // read back. So the data was right, the markup was right, and this line was early.
        //
        // Consequence for the shopper: a sold-out finish looked identical to an in-stock one —
        // no strike-through — until they selected it and hit SOLD OUT. Founder spotted it as
        // "out of stokc swatches hover tooltop is difeetnt ??".
        //
        // Same shape as the Add-to-Cart "Sold out" defect fixed the same day: the server was
        // correct and JS clobbered it after load. See skill rule 6j.
        if (trustMarkup) return;

        var opt = optionFor(radio.value);
        if (!opt) return;
        var sold = opt.disabled || opt.classList.contains('soldout');
        label.classList.toggle('soldout', sold);
        label.classList.toggle('available', !sold);
      });

      if (current) current.textContent = value;
    }

    // swatches -> select
    strip.addEventListener('change', function (event) {
      var radio = event.target;
      if (!radio || !radio.classList || !radio.classList.contains('lfy-cswatch__radio')) return;
      if (select.value === radio.value) return;

      select.value = radio.value;
      // bubbles, because <variant-selects> listens on ITSELF, not on the select.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Two listeners, on purpose. The capture-phase one repaints the strip immediately so a
    // tap feels instant; the delayed one re-reads availability AFTER variants.js has
    // finished rewriting the <option> classes and text in its own bubble-phase handler.
    select.addEventListener('change', sync);
    picker.addEventListener('change', function () { window.setTimeout(sync, 80); });

    // ── LAST, and only now ────────────────────────────────────────────────────────
    // Retire the native dropdown only once the strip is provably wired to it. Every
    // `return warn(...)` above leaves this line unreached and the dropdown visible.
    // display:none keeps the node in the DOM, which variants.js requires — it writes to
    // [data-header-option] inside this wrapper on every change.
    wrap.setAttribute('data-lfy-swatched', 'true');

    strip.dataset[BOUND] = '1';

    // Selection and label only — the server's availability classes are already correct and
    // variants.js has not classed the <option>s yet.
    sync({ trustMarkup: true });

    // Then re-read ONCE, after variants.js has had a turn, so a product whose availability
    // Liquid could not know (multi-option combinations) still converges. Late enough to be
    // after Ella's init, and idempotent — it only ever re-toggles two classes.
    window.setTimeout(sync, 400);
  }

  function init(root) {
    var strips = (root || document).querySelectorAll('[data-lfy-color-swatch]');
    Array.prototype.forEach.call(strips, bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // <variant-selects> is a custom element; on a slow parse it can upgrade after this
  // script runs, which would make the `picker` lookup above fail. Re-running init once
  // the definition is ready is idempotent (the BOUND flag makes bind a no-op).
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('variant-selects').then(function () { init(); });
  }

  // Section re-render in the theme editor.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
